import { useAuth, useClerk } from '@clerk/react'
import { Plus, Star } from 'lucide-react'
import { useCallback, useState } from 'react'
import { CreateListSlide } from '@/components/problem-lists/create-list-slide'
import { useProblemListsOptional } from '@/components/problem-lists/problem-list-context'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface StarDropdownProps {
  problemId: string
}

export function StarDropdown({ problemId }: StarDropdownProps) {
  const { isSignedIn } = useAuth()
  const { openSignIn } = useClerk()
  const ctx = useProblemListsOptional()

  const [open, setOpen] = useState(false)
  const [showCreateSlide, setShowCreateSlide] = useState(false)

  const isStarred = ctx?.isInAnyList(problemId) ?? false
  const listsForProblem = ctx?.getListsForProblem(problemId) ?? []

  const handleStarClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      if (!isSignedIn) {
        openSignIn()
        return
      }

      if (!isStarred && ctx && ctx.lists.length === 1 && ctx.favoritesId) {
        ctx.toggleProblemInList(ctx.favoritesId, problemId)
      }
      setOpen(true)
    },
    [isSignedIn, openSignIn, isStarred, ctx, problemId],
  )

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setShowCreateSlide(false)
    }
  }, [])

  // Logged-out users: plain star button that opens sign-in
  if (!isSignedIn) {
    return (
      <button
        type="button"
        aria-label="Add to list"
        onClick={(e) => {
          e.stopPropagation()
          openSignIn()
        }}
        className="ml-auto block hover:text-yellow-400"
      >
        <Star className="text-muted-foreground h-4 w-4" />
      </button>
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={isStarred ? 'Manage lists' : 'Add to list'}
          onClick={handleStarClick}
          className="ml-auto block hover:text-yellow-400"
          data-problem-id={problemId}
        >
          <Star
            className={cn(
              'h-4 w-4',
              isStarred
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="star-dropdown-popover relative w-56 overflow-hidden p-0"
        onClick={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Main list panel */}
        <div className="p-4">
          <p className="mb-2 text-sm font-medium">My Lists</p>
          <div className="flex flex-col gap-1">
            {ctx?.lists.map((list) => {
              const checked = listsForProblem.includes(list.id)
              return (
                <label
                  key={list.id}
                  className="hover:bg-muted/50 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      e.stopPropagation()
                      ctx?.toggleProblemInList(list.id, problemId)
                    }}
                    className="accent-primary h-4 w-4"
                  />
                  {list.name}
                </label>
              )
            })}
          </div>
          <div className="border-border my-2 border-t" />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setShowCreateSlide(true)
            }}
            className="text-muted-foreground hover:text-foreground flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create a new list
          </button>
        </div>

        {/* Create list slide */}
        <CreateListSlide
          visible={showCreateSlide}
          onCreated={() => {
            setShowCreateSlide(false)
          }}
          onCancel={() => setShowCreateSlide(false)}
          createList={ctx?.createList ?? (async () => null)}
        />
      </PopoverContent>
    </Popover>
  )
}
